import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import { Label } from '@/components/ui/label'
import { z } from 'zod'
import Select from 'app/frontend/base/components/Select'
import FormWrapper from 'app/frontend/base/components/FormWrapper'
import TextField from 'app/frontend/base/components/form-fields/TextField'
import { Button } from '@/components/ui/button'
import { validateCustomDomainHost } from 'app/frontend/base/api/team'
import { validateGuildCustomDomainHost } from 'app/frontend/base/api/guild'
import CustomDomainHostStatus from 'app/frontend/types/custom/CustomDomainHostStatuses'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  AlertDescription,
  AlertTitle,
  alertVariants,
} from '@/components/ui/alert'
import { cn } from '@/lib/utils'
import { VariantProps } from 'class-variance-authority'
import { CheckCircleIcon, XCircleIcon } from 'lucide-react'
import { useDebounce } from 'react-use'
import { CustomDomainOwner } from './CustomDomainHosts'

export const MAX_HOSTS = 5

type DomainType = 'subdomain' | 'own'

const DOMAIN_TYPE_OPTIONS = [
  { label: 'wowaudit subdomain', value: 'subdomain' },
  { label: 'Own domain', value: 'own' },
]

const formSchema = z.object({
  customDomainAttributes: z.object({
    id: z.number().optional(),
    hostsAttributes: z
      .array(
        z.object({
          host: z.string().optional(),
          subdomain: z.string().optional(),
          _destroy: z.boolean().optional(),
        }),
      )
      .refine((hosts) => hosts.filter((h) => !h._destroy).length <= MAX_HOSTS, {
        message: `You can have at most ${MAX_HOSTS} hosts.`,
      }),
  }),
})

interface Props {
  open: boolean
  setOpen: (open: boolean) => void
  entityType: 'team' | 'guild'
  entity: CustomDomainOwner
}

export const variantForStatus: Record<
  CustomDomainHostStatus,
  VariantProps<typeof alertVariants>['variant']
> = {
  active: 'success',
  available: 'success',
  taken: 'destructive',
  blacklisted: 'destructive',
  invalid_format: 'destructive',
  dns_pending: 'warning',
  cert_pending: 'warning',
}

const CustomDomainNewHostModal = ({
  open,
  setOpen,
  entityType,
  entity,
}: Props) => {
  const [validation, setValidation] = useState<CustomDomainHostStatus>()

  const maxHostsReached =
    (entity.customDomainAttributes?.hostsAttributes?.length ?? 0) >= MAX_HOSTS

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent onInteractOutside={(e) => e.preventDefault()}>
        <FormWrapper
          formSchema={formSchema}
          entityType={entityType}
          entity={{
            ...entity,
            customDomainAttributes: {
              ...entity.customDomainAttributes,
              hostsAttributes: [
                {
                  subdomain: '',
                  host: '',
                },
              ],
            },
          }}
          onSave={() => {
            setOpen(false)
          }}
          submitButtons={({ isPending }) => (
            <>
              <Button
                type='button'
                size='sm'
                variant='outline'
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                size='sm'
                loading={isPending}
                disabled={validation !== 'available' || maxHostsReached}
              >
                Add domain
              </Button>
            </>
          )}
        >
          {maxHostsReached ? (
            <>
              <DialogHeader>
                <DialogTitle>Add domain</DialogTitle>
              </DialogHeader>
              <Alert className='mt-6'>
                <AlertTitle>Maximum hosts reached</AlertTitle>
                <AlertDescription>
                  You can have at most {MAX_HOSTS} domains configured at the
                  same time.
                </AlertDescription>
              </Alert>
            </>
          ) : (
            <CustomDomainNewHostContent
              validation={validation}
              setValidation={setValidation}
              entityType={entityType}
              entity={entity}
            />
          )}
        </FormWrapper>
      </DialogContent>
    </Dialog>
  )
}

const CustomDomainNewHostContent = ({
  validation,
  setValidation,
  entityType,
  entity,
}: {
  validation?: CustomDomainHostStatus
  setValidation: (validation?: CustomDomainHostStatus) => void
  entityType: 'team' | 'guild'
  entity: CustomDomainOwner
}) => {
  const [domainType, setDomainType] = useState<DomainType>('subdomain')
  const { t } = useTranslation()
  const form = useFormContext()
  const field = domainType === 'subdomain' ? 'subdomain' : 'host'
  const fieldValue = form.watch(
    `customDomainAttributes.hostsAttributes.0.${field}`,
  )

  useDebounce(
    async () => {
      if (!fieldValue) {
        setValidation(undefined)
        return
      }
      const result =
        entityType === 'team'
          ? await validateCustomDomainHost(entity.id, { [field]: fieldValue })
          : await validateGuildCustomDomainHost(entity.id, {
              [field]: fieldValue,
            })
      setValidation(result.status)
    },
    500,
    [fieldValue],
  )

  const alertVariant = validation ? variantForStatus[validation] : 'info'

  return (
    <>
      <DialogHeader>
        <DialogTitle>Add domain</DialogTitle>
      </DialogHeader>

      <div className='flex flex-col gap-4 mt-6'>
        <div className='flex flex-col gap-2'>
          <Label>Type</Label>
          <Select
            value={domainType}
            inModal
            setValue={(v) => {
              setDomainType(v as DomainType)
              form.setValue('customDomainAttributes.hostsAttributes.0.host', '')
              form.setValue(
                'customDomainAttributes.hostsAttributes.0.subdomain',
                '',
              )
              setValidation(undefined)
            }}
            options={DOMAIN_TYPE_OPTIONS}
            className='bg-card hover:bg-card'
          />
        </div>

        {domainType === 'subdomain' ? (
          <div className='flex items-center gap-1 w-full mb-5'>
            <TextField
              layout='inline-shrinkable'
              name={'customDomainAttributes.hostsAttributes.0.subdomain'}
              inputClassName='text-right bg-card hover:bg-card'
              customLabel='Name'
              placeholder='subdomain'
            />
            <div className='mt-[18px] text-muted-foreground'>.wowaudit.com</div>
          </div>
        ) : (
          <TextField
            name={'customDomainAttributes.hostsAttributes.0.host'}
            customLabel='Domain name'
            inputClassName='bg-card hover:bg-card'
            placeholder='example.com'
          />
        )}
      </div>

      <Alert
        variant={alertVariant}
        className={cn(
          'transition-opacity',
          validation ? 'opacity-100' : 'opacity-50',
        )}
      >
        {validation ? (
          <AlertDescription className='flex items-center gap-2'>
            {alertVariant === 'success' ? (
              <CheckCircleIcon size={16} />
            ) : (
              <XCircleIcon size={16} />
            )}
            {t(`errors.custom_domain.host_statuses.${validation}`)}
          </AlertDescription>
        ) : (
          <AlertDescription className='flex items-center gap-2'>
            Please enter a valid domain name or subdomain.
          </AlertDescription>
        )}
      </Alert>
    </>
  )
}

export default CustomDomainNewHostModal
